import CarDetails from '@common/pages/Dashboard/components/CarDetails';
import CarDetailSkeleton from '@common/pages/Dashboard/components/CarDetailsSkeleton';
import SaleInformation from '@common/pages/Dashboard/components/SaleInformation';
import { Box, Button, CircularProgress, Divider, Grid, Typography, useMediaQuery } from '@mui/material';
import { useFormik } from 'formik';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import theme from '../../../../theme';
import { setAlertDialog } from '../../../../toolkit/alertDialog/alertDialogSlice';
import { useLazyGetBidHistoryQuery } from '../../../../toolkit/liveAuction/liveAuctionApi';
import { deliveryModes } from '../../../../utils/constants/deliveryModes';
import { mainContainer } from '../VehicleInfoSidebarSx';
// import { useLiveAuctionSocketEvents } from "./customHooks/useLiveAuctionSocketEvents";
import { useCountdownTimer } from '@common/customHooks/useCountdownTimer';
import ReservePriceMetCard from '@common/pages/Dashboard/components/LiveAuction/components/ReservePriceOrNearReserverPriceCard';
import TimeLeft from '@common/pages/Dashboard/components/TimeLeft';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { reducerPaths } from '../../../../app/reducerPaths';
import PriceInput from '../../../../components/custom/CustomInputs/PriceInput';
import {
  useLazyGetBuyNowTimeoutDetailsQuery,
  useSetBidPriceBuyNowMutation,
} from '../../../../toolkit/buyNow/buyNowApi';
import { useLazyGetAgentDetailsQuery } from '../../../../toolkit/carDetails/carDetailsApi';
import { useLazyGetVehicleQuery } from '../../../../toolkit/vehicle/vehicleApi';
import { Bidding_Through } from '../../../../utils/constants/auction';
import { routes } from '../../../../utils/constants/routes';
import { getImagesHandler } from '../../../../utils/methods/setSellMyCarForms';
import { getCommaFormattedPrice } from '../../../../utils/methods/stringMethods';
import { useBuyNowSocketEvents } from './customHooks/useBuyNowAuctionSocketEvents';
import { useBuyNow } from './handlers/useBuyNow';
import { USER_ROLES } from 'src/utils/constants/users/userRoles';
import CostBreakdown from "@common/components/custom/CostBreakdownSection/CostBreakdownSection";
import { isVehicleSold } from '@common/utils/methods/generalVehicleMethods';

// const TimeLeftInBuyNow = ({ data }) => {
//   const { t } = useTranslation('buyNowCarDetailReadOnly');
//   const { countDown, isInitialised } = useCountdownTimer({
//     seconds: Math.floor(data?.seconds || 0),
//   });

//   return (
//     <Box
//       sx={{
//         backgroundColor: theme.palette.background.paper,
//         border: `2px solid ${theme.palette.primary.main}`,
//         textAlign: 'center',
//         borderRadius: '0.75rem', // 12px
//         paddingTop: '1rem', // 16px
//         paddingBottom: '1rem',
//         paddingLeft: '0.5rem', // 8px
//         paddingRight: '0.5rem',
//       }}
//     >
//       {(countDown?.hours > 0 || countDown?.minutes > 0 || countDown?.seconds > 0) && isInitialised ? (
//         <Typography
//           fontWeight={600}
//           fontSize="1.1rem"
//           sx={{
//             color: theme.palette.primary.main,
//           }}
//         >
//           {t('Time Left In Buy Now')}
//         </Typography>
//       ) : null}
//       <Typography
//         fontWeight={500}
//         fontSize="1rem"
//         marginTop="0.25rem"
//         sx={{
//           color: theme.palette.primary.main,
//         }}
//       >
//         {!isInitialised ? (
//           <span style={{ fontWeight: 700, color: theme.palette.primary.main }}>
//             Loading...
//           </span>
//         ) : countDown?.hours > 0 || countDown?.minutes > 0 || countDown?.seconds > 0 ? (
//           <>
//             <span style={{ fontWeight: 800, color: theme.palette.primary.main }}>
//               {`${countDown?.hours < 10 ? `0${countDown?.hours}` : countDown?.hours}`}
//             </span>{' '}
//             {t('hours')}{' '}
//             <span style={{ fontWeight: 800, color: theme.palette.primary.main }}>
//               {`${countDown?.minutes < 10 ? `0${countDown?.minutes}` : countDown?.minutes}`}
//             </span>{' '}
//             {t('mins')}{' '}
//             <span style={{ fontWeight: 800, color: theme.palette.primary.main }}>
//               {`${countDown?.seconds < 10 ? `0${countDown?.seconds}` : countDown?.seconds}`}
//             </span>{' '}
//             {t('sec')}
//           </>
//         ) : (
//           <span style={{ fontWeight: 700, color: theme.palette.primary.main }}>{t("Time's up")}</span>
//         )}
//       </Typography>
//     </Box>
//   );
// };

const ReservePrice = ({ selectedCar, price = '38,000' }) => {
  const { t } = useTranslation('buyNowCarDetailReadOnly');

  const navigate = useNavigate();

  const { buyNowHandler } = useBuyNow({});

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1.4rem',
        padding: '1.5rem 1rem',
        boxShadow: '0 0.25rem 0.5rem rgba(0, 0, 0, 0.1)', // Example shadow
      }}
    >
      <Typography
        variant="h4" // Using h4 as it seems appropriate for the size in the screenshot
        sx={{
          color: theme.palette.text.secondary, // White text as per screenshot
          backgroundColor: theme.palette.primary.main, // Blue background
          paddingTop: '0.75rem',
          paddingBottom: '0.75rem',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem',
          borderRadius: '0.5rem', // Rounded corners for the price box
          fontWeight: '700',
          marginBottom: '1rem', // Space between price and button
        }}
      >
        ${getCommaFormattedPrice(price)} *
      </Typography>
      <Button
        variant="contained"
        sx={{
          width: '100%',
          backgroundColor: theme.palette.background.paper, // White background
          color: theme.palette.primary.main, // Blue text
          fontWeight: '600',
          borderRadius: '0.5rem',
          paddingTop: '0.75rem',
          paddingBottom: '0.75rem',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem',
          textTransform: 'none',
          border: `0.0625rem solid ${theme.palette.primary.main}`, // Blue border
          '&:hover': {
            backgroundColor: theme.palette.background.default, // Light grey on hover
          },
        }}
        onClick={() => {
          buyNowHandler({
            Vehicle_Id: selectedCar?.Vehicle_Id,
            onCloseCb: () => {
              navigate(`${routes.dashboardNavigation}${routes.buyNow}`);
            },
          });
        }}
      >
        {t('Buy Now')}
      </Button>
    </Box>
  );
};

const CurrentHighestOffer = ({ highestBid }) => {
  const { t } = useTranslation('buyNowCarDetailReadOnly');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: theme.palette.primary.red,
        borderRadius: '1.4rem',
        padding: '1.5rem',
        boxShadow: '0 0.25rem 0.5rem rgba(0, 0, 0, 0.1)', // Example shadow
      }}
    >
      <Typography
        variant="body1"
        sx={{
          color: theme.palette.text.secondary, // White text as per screenshot
          padding: '0.75rem',
          borderRadius: '0.5rem', // Rounded corners for the price box
          fontWeight: '700',
          textAlign: 'center',
        }}
      >
        {t('Current offer')}
      </Typography>

      <Typography
        variant="body2" // Using h4 as it seems appropriate for the size in the screenshot
        sx={{
          color: theme.palette.text.secondary, // White text as per screenshot
          padding: '0.75rem',
          borderRadius: '0.5rem', // Rounded corners for the price box
          fontSize: '2.5rem',
          fontWeight: '700',
          textAlign: 'center',
        }}
      >
        {highestBid?.Bid_Amount ? `$${getCommaFormattedPrice(highestBid?.Bid_Amount)}` : t('No offers yet')}
      </Typography>
    </Box>
  );
};

const BidInformation = ({ selectedCar = {}, highestBid, bidHistory, refetchSelectedCar, setRefetchSelectedCar }) => {
  const { t } = useTranslation('bidInformation');

  // static data start...
  const makeOfferInitialValues = React.useMemo(() => {
    return {
      Bid_Amount: '',
      delivery_mode: deliveryModes.Pickup,
    };
  }, []);
  // static data end...

  const isMobile = useMediaQuery('(max-width:1300px)');

  const navigate = useNavigate();

  // useDispatch...
  const dispatch = useDispatch();
  // useDispatch...

  const auth = useSelector((store) => store[reducerPaths.auth]);

  // useState variables start...
  // const [isVehicleSold, setIsVehicleSold] = React.useState(false);
  // useState variables end...

  // custom hooks start...
  const [setBidPriceBuyNowApiHandler, setDealerBidPriceBuyNowApiData] = useSetBidPriceBuyNowMutation();
  // custom hooks end...

  // place bid form schema...
  const makeOfferSchema = Yup.object().shape({
    Bid_Amount: Yup.number().required(),
    delivery_mode: Yup.string().oneOf([deliveryModes.Pickup, deliveryModes.Dropoff]).required(),
  });

  const makeOfferFormik = useFormik({
    initialValues: makeOfferInitialValues,
    validationSchema: makeOfferSchema,
    onSubmit: async (values) => {
      if (highestBid && values?.Bid_Amount <= highestBid) {
        dispatch(
          setAlertDialog({
            open: true,
            title: t('Error!'),
            message: t('Please enter a bid greater than the current highest bid!'),
          })
        );

        return;
      }

      const formData = new FormData();

      for (let i = 0; i < Object.entries(values)?.length; i++) {
        formData.append(Object.entries(values)?.[i]?.[0], Object.entries(values)?.[i]?.[1]);
      }

      formData.append(`Vehicle_Id`, selectedCar?.Vehicle_Id);

      // call "setBidPriceBuyNowApiHandler" here...
      const res = await setBidPriceBuyNowApiHandler({
        payload: formData,
      });

      if (res?.error || ('status' in res?.data && !res?.data?.status)) {
        dispatch(
          setAlertDialog({
            open: true,
            title: t('Error!'),
            message: res?.error?.data?.error || res?.data?.message || t('Some error occured!'),
          })
        );
      } else if (res?.data) {
        makeOfferFormik.resetForm();
        if (res?.data?.status === 'bid_sold') {
          // setIsVehicleSold(true);
          setRefetchSelectedCar((prev) => prev + 1);
          dispatch(
            setAlertDialog({
              open: true,
              title: t('Info!'),
              message: res?.data?.message,
              onClose: () => {
                navigate(`${routes.dashboardNavigation}${routes.buyNow}`);
              },
            })
          );
        }
      }
    },
  });
  // place bid form schema...

  const getYourHighestOffer = () => {
    const yourHighestOffer = bidHistory
      ?.filter?.((offer) => {
        return offer?.Dealer_Id === auth?.user_id;
      })
      ?.map((offer) => {
        return {
          ...offer,
          Bid_Amount: parseInt(offer?.Bid_Amount),
        };
      })
      ?.sort?.((a, b) => {
        if (a?.Bid_Amount < b?.Bid_Amount) {
          return 1;
        }
        if (a?.Bid_Amount > b?.Bid_Amount) {
          return -1;
        }
      })?.[0]?.Bid_Amount;

    return yourHighestOffer;
  };

  const getNegotiationSuggestions = () => {
    const reservePrice = parseInt(selectedCar?.Reserve_Price);

    const dealerLastOffer = getYourHighestOffer() || highestBid;

    const dealerStart = dealerLastOffer;

    if (!dealerStart) {
      return null;
    }

    // Define jump slabs based on reserve price
    const getSteps = (price) => {
      if (price < 5000) return [100, 200, 300];
      if (price < 10000) return [200, 300, 500];
      if (price < 20000) return [200, 300, 500];
      if (price < 50000) return [500, 1000, 1500];
      return [1000, 1500, 2000];
    };

    const [step1, step2, step3] = getSteps(reservePrice);

    const dealerSuggestions = [dealerStart + step1, dealerStart + step2, dealerStart + step3];

    return {
      offer1: Math.floor(dealerSuggestions[0]),
      offer2: Math.floor(dealerSuggestions[1]),
      offer3: Math.floor(dealerSuggestions[2]),
    };
  };

  const handleConditionsBeforeSubmit = () => {
    if (makeOfferFormik?.values?.Bid_Amount > parseInt(selectedCar?.Reserve_Price)) {
      dispatch(
        setAlertDialog({
          open: true,
          title: `Alert!`,
          message: 'Your offer exceeds the reserve price. Are you sure you want to make this offer?',
          onOk: () => {
            makeOfferFormik?.handleSubmit();
          },
        })
      );
    } else {
      makeOfferFormik?.handleSubmit();
    }
  };

  return (
    // Add inside BuyNowCarDetail component, before <BidHistorySection />
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        borderRadius: '0.75rem',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* Place Your Bid */}
      {!(selectedCar?.Sold_Out === 1) ? (
        <Box
          sx={{
            backgroundColor: '#F8F8F8',
            borderRadius: '0.75rem',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {getNegotiationSuggestions() ? (
            <Box sx={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                sx={{
                  borderColor: theme.palette.border.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.text.secondary,
                  },
                  borderRadius: '0.5rem',
                  backgroundColor: 'transparent',
                }}
                onClick={() => {
                  makeOfferFormik.setValues((values) => {
                    return {
                      ...values,
                      Bid_Amount: getNegotiationSuggestions().offer1,
                    };
                  });
                }}
              >
                {t('Offer')} ${getNegotiationSuggestions().offer1}
              </Button>
              <Button
                variant="outlined"
                sx={{
                  borderColor: theme.palette.border.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.text.secondary,
                  },
                  borderRadius: '0.5rem',
                  backgroundColor: 'transparent',
                }}
                onClick={() => {
                  makeOfferFormik.setValues((values) => {
                    return {
                      ...values,
                      Bid_Amount: getNegotiationSuggestions().offer2,
                    };
                  });
                }}
              >
                {t('Offer')} ${getNegotiationSuggestions().offer2}
              </Button>
              <Button
                variant="outlined"
                sx={{
                  borderColor: theme.palette.border.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.text.secondary,
                  },
                  borderRadius: '0.5rem',
                  backgroundColor: 'transparent',
                }}
                onClick={() => {
                  makeOfferFormik.setValues((values) => {
                    return {
                      ...values,
                      Bid_Amount: getNegotiationSuggestions().offer3,
                    };
                  });
                }}
              >
                {t('Offer')} ${getNegotiationSuggestions().offer3}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                sx={{
                  borderColor: theme.palette.border.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.text.secondary,
                  },
                  borderRadius: '0.5rem',
                  backgroundColor: 'transparent',
                }}
                onClick={() => {
                  makeOfferFormik.setValues((values) => {
                    return {
                      ...values,
                      Bid_Amount: parseInt(selectedCar?.Reserve_Price),
                    };
                  });
                }}
              >
                {t('Offer')} ${selectedCar?.Reserve_Price}
              </Button>
            </Box>
          )}

          <PriceInput
            placeholder={t(`$ ${getCommaFormattedPrice(0.7 * selectedCar?.Reserve_Price)}`)}
            fullWidth
            size="medium"
            name={'Bid_Amount'}
            value={makeOfferFormik?.values?.Bid_Amount}
            onChange={makeOfferFormik.handleChange}
            sx={{
              input: {
                fontWeight: 600,
                '&::placeholder': {
                  color: `${theme.palette.text.disabled} !important`,
                  opacity: 1,
                },
              },
              backgroundColor: theme.palette.background.paper,
              borderRadius: '0.5rem',
            }}
          />

          {/* {setDealerBidPriceBuyNowApiData?.data?.status && setDealerBidPriceBuyNowApiData?.isSuccess ? (
            <Typography align="center" fontWeight={600} color="green" fontSize="0.9rem">
              {t('Offer sent successfully')}
            </Typography>
          ) : null} */}

          {/* Submit Button */}
          <Button
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.primary.main,
              border: `1px solid ${theme.palette.primary.main}`,
              borderRadius: '0.5rem',
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.primary.main,
                border: `1px solid ${theme.palette.primary.main}`,
              },
            }}
            onClick={handleConditionsBeforeSubmit}
            disabled={setDealerBidPriceBuyNowApiData?.isLoading}
          >
            {setDealerBidPriceBuyNowApiData?.isLoading ? (
              <CircularProgress
                size={22}
                sx={{
                  color: theme.palette.background.paper,
                }}
              />
            ) : (
              t('Make offer')
            )}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

const CarSoldTag = ({ soldPrice }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.palette.background.default,
        padding: '1.5rem', // Overall padding around the content
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: '1.25rem', // Rounded corners for the main card
          padding: '1.5rem', // Inner padding for the main card
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem', // Space between the two inner boxes
          width: '100%', // Take full width of parent
          maxWidth: '25rem', // Max width for desktop view
          boxShadow: '0rem 0.25rem 0.625rem rgba(0, 0, 0, 0.1)', // Subtle shadow
        }}
      >
        {/* Car Sold Section */}
        <Box
          sx={{
            backgroundColor: 'rgba(43, 210, 76, 0.1)', // Light green background (10% opacity of primary.green)
            border: `0.0625rem solid ${theme.palette.primary.green}`, // Green border
            borderRadius: '0.625rem', // Rounded corners
            paddingTop: '1.5rem',
            paddingBottom: '1.5rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="body1" // Use body1 variant for text
            sx={{
              color: theme.palette.primary.green, // Green text color
              fontWeight: 600, // Make text bold
              fontSize: '1.125rem', // Custom font size for this text
            }}
          >
            Car Sold
          </Typography>
        </Box>

        {/* Sold Price Section */}
        <Box
          sx={{
            backgroundColor: theme.palette.primary.green, // Darker green background
            borderRadius: '0.625rem', // Rounded corners
            paddingTop: '1.5rem',
            paddingBottom: '1.5rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body1" // Use body1 for "Sold Price"
            sx={{
              color: theme.palette.text.secondary, // White text
              fontWeight: 500,
              fontSize: '1rem',
              marginBottom: '0.5rem', // Space below "Sold Price"
            }}
          >
            Sold Price
          </Typography>
          <Typography
            variant="h6" // Using h6 for the price to make it larger and bolder
            sx={{
              color: theme.palette.text.secondary, // White text
              fontWeight: 700, // Extra bold for the price
              fontSize: '1.75rem', // Larger font size for the price
            }}
          >
            {`$${getCommaFormattedPrice(soldPrice)}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Main Car Detail Page component
const BuyNowCarDetail = (
  {
    // selectedCar,
    // setSelectedCar
  }
) => {
  const { t } = useTranslation('buyNowCarDetailReadOnly');

  const navigate = useNavigate();

  // useLocation...
  const location = useLocation();
  // useLocation...

  const Vehicle_Id = location?.pathname?.split?.(`${routes.buyNow}/`)?.[1];

  const isMobile = useMediaQuery('(max-width:1550px)');

  // useDispatch...
  const dispatch = useDispatch();
  // useDispatch...

  // useSelector start...
  const buyNow = useSelector((store) => store[reducerPaths.buyNow]);
  const auth = useSelector((store) => store[reducerPaths.auth]);
  // useSelector end...

  // useState variables start...
  const [selectedCar, setSelectedCar] = React.useState(null);
  const [buyNowRemainingSeconds, setBuyNowRemainingSeconds] = React.useState(0);
  // const [isVehicleSold, setIsVehicleSold] = React.useState(false);
  const [refetchSelectedCar, setRefetchSelectedCar] = React.useState(0);
  // useState variables end...

  // Handlers start...
  const parsedVehicle = (selectedCar) => {
    const images = [];
    const carImages = getImagesHandler({
      data: {
        vehicle: selectedCar,
      },
    });

    for (let i = 0; i < Object.entries(carImages?.images)?.length; i++) {
      for (let j = 0; j < Object.entries(carImages?.images)?.[i]?.[1]?.length; j++) {
        if (Object.entries(carImages?.images)?.[i]?.[1]?.[j]) {
          images.push(Object.entries(carImages?.images)?.[i]?.[1]?.[j]);
        }
      }
    }

    return {
      ...selectedCar,
      images,
    };
  };

  const onNewBidHandler = async () => {
    await getBidHistoryApiHandler({
      vehicleId: selectedCar?.Vehicle_Id,
    });
  };
  // Handlers end...

  // custom hooks start...
  const [getBidHistoryApiHandler, getBidHistoryApiData] = useLazyGetBidHistoryQuery();
  const [getVehicleApiHandler, getVehicleApiData] = useLazyGetVehicleQuery();
  const [getAgentDetailsApiHandler, getAgentDetailsApiData] = useLazyGetAgentDetailsQuery();
  const [getBuyNowTimeoutDetailsApiHandler, getBuyNowTimeoutDetailsApiData] = useLazyGetBuyNowTimeoutDetailsQuery();

  useBuyNowSocketEvents({
    Vehicle_Id: selectedCar?.Vehicle_Id,
    onNewBidHandler,
  });

  const { countDown, isInitialised } = useCountdownTimer({
    seconds: buyNowRemainingSeconds,
  });
  // custom hooks end...

  const highestBid = getBidHistoryApiData?.data?.TopBids?.map?.((bid) => {
    return {
      ...bid,
      Bid_Amount: parseInt(bid?.Bid_Amount),
    };
  })?.filter?.((bid) => {
    return bid?.Bidding_Through === Bidding_Through.BuyNow;
  })?.[0];

  // useEffect start...
  React.useEffect(() => {
    if (!selectedCar?.Vehicle_Id) {
      return;
    }

    (async () => {
      const res = await getBidHistoryApiHandler({
        vehicleId: selectedCar?.Vehicle_Id,
      });

      if (res?.error) {
        dispatch(
          setAlertDialog({
            open: true,
            title: `Error!`,
            message: res?.error?.data?.message || res?.error?.data?.error || `Some error occured!`,
          })
        );
      }
    })();
  }, [selectedCar?.Vehicle_Id]);

  React.useEffect(() => {
    if (Vehicle_Id) {
      (async () => {
        const res = await getVehicleApiHandler({
          Vehicle_Id,
        });

        if (res?.error) {
          dispatch(
            setAlertDialog({
              open: true,
              title: `Error`,
              message: res?.error?.data?.error || res?.error?.data?.message || `Some error occured!`,
            })
          );
        } else {
          setSelectedCar(res?.data?.vehicle);
        }
      })();
    }
  }, [Vehicle_Id, refetchSelectedCar]);

  React.useEffect(() => {
    if (Vehicle_Id) {
      (async () => {
        const res = await getAgentDetailsApiHandler({
          vehicleId: Vehicle_Id,
        });

        if (res?.error) {
          dispatch(
            setAlertDialog({
              open: true,
              title: `Error`,
              message: res?.error?.data?.error || res?.error?.data?.message || `Some error occured!`,
            })
          );
        }
      })();
    }
  }, [Vehicle_Id]);

  React.useEffect(() => {
    if (Vehicle_Id) {
      (async () => {
        const res = await getBuyNowTimeoutDetailsApiHandler({
          Vehicle_Id,
        });

        if (res?.error || ('status' in res?.data && !res?.data?.status)) {
          dispatch(
            setAlertDialog({
              open: true,
              title: `Error`,
              message:
                res?.error?.data?.error || res?.error?.data?.message || res?.data?.message || `Some error occured!`,
            })
          );
        } else {
          setBuyNowRemainingSeconds(res?.data?.counterseconds || 0);
        }
      })();
    }
  }, [Vehicle_Id]);

  React.useEffect(() => {
    if (parseInt(getBidHistoryApiData?.data?.TopBids?.[0]?.Bid_Amount) >= parseInt(selectedCar?.Reserve_Price)) {
      // setIsVehicleSold(true);
      setRefetchSelectedCar((prev) => prev + 1);
      // if (getBidHistoryApiData?.data?.TopBids?.[0]?.Dealer_Id !== auth?.user_id) {
      //   dispatch(
      //     setAlertDialog({
      //       open: true,
      //       title: `Info!`,
      //       message: `The car has been sold to dealer id: ${getBidHistoryApiData?.data?.TopBids?.[0]?.Dealer_Id}`,
      //       onClose: () => {
      //         navigate(routes.dashboardNavigation);
      //       }
      //     })
      //   )
      // }
    }
  }, [getBidHistoryApiData?.data?.TopBids]);

  React.useEffect(() => {
    if (countDown?.hours === 0 && countDown?.minutes === 0 && countDown?.minutes === 0 && isInitialised) {
      if (isVehicleSold({ car: selectedCar }) && parseInt(highestBid?.Dealer_Id) === auth?.user_id) {
        dispatch(
          setAlertDialog({
            open: true,
            title: `Success!`,
            message: `Congratulations! You have won the auction. Would you like to go to Your Orders page?`,
            onOk: () => {
              navigate(`${routes.dashboardNavigation}${routes.myOrders}`);
            }
          })
        )
      } else if (isVehicleSold({ car: selectedCar })) {
        dispatch(
          setAlertDialog({
            open: true,
            title: `Alert!`,
            message: `The auction time is over. Would you like to go to the Buy now page?`,
            onOk: () => {
              navigate(`${routes.dashboardNavigation}${routes.buyNow}`);
            }
          })
        )
      }
    }
  }, [
    selectedCar,
    highestBid
  ]);
  // useEffect end...

  return (
    <Grid container spacing={1} sx={mainContainer}>
      {getVehicleApiData?.isFetching ? (
        <CarDetailSkeleton />
      ) : (
        <>
          {/* Left sidebar with buttons */}
          <Grid size={{ xl: 2.8 - 0.2, lg: 4 - 0.2, md: 12, sm: 12, xs: 12 }}>
            <Box display={'flex'} flexDirection="column" gap={2}>
              {/* <TimeLeftInBuyNow /> */}
              {selectedCar?.Sold_Out === 0 ? (
                <TimeLeft
                  data={{
                    seconds: buyNowRemainingSeconds,
                  }}
                />
              ) : null}

              {selectedCar?.Sold_Out === 1 ? (
                <CarSoldTag soldPrice={parseInt(getBidHistoryApiData?.data?.TopBids?.[0]?.Bid_Amount)} />
              ) : parseInt(selectedCar?.Reserve_Price) <= highestBid?.Bid_Amount ||
                parseInt(selectedCar?.Reserve_Price) <= highestBid?.Bid_Amount + 500 ? (
                <>
                  <ReservePriceMetCard car={selectedCar} highestBid={highestBid} />
                </>
              ) : null}

              {!(selectedCar?.Sold_Out === 1) ? (
                <ReservePrice selectedCar={selectedCar} price={selectedCar?.Reserve_Price} />
              ) : null}

              <CurrentHighestOffer
                highestBid={
                  getBidHistoryApiData?.data?.TopBids?.filter?.((bid) => {
                    return bid?.Bidding_Through === Bidding_Through.BuyNow;
                  })?.[0]
                }
              />

              {(countDown?.hours > 0 || countDown?.minutes > 0 || countDown?.seconds > 0) &&
              !(selectedCar?.Sold_Out === 1) ? (
                <BidInformation
                  selectedCar={selectedCar}
                  bidHistory={buyNow?.bidHistory?.data || []}
                  highestBid={
                    (buyNow?.bidHistory?.data || [])
                      ?.map?.((bid) => {
                        return {
                          ...bid,
                          Bid_Amount: parseInt(bid?.Bid_Amount),
                        };
                      })
                      ?.sort?.((bidA, bidB) => {
                        const bidAAmount = parseInt(bidA?.Bid_Amount);
                        const bidBAmount = parseInt(bidB?.Bid_Amount);

                        if (bidAAmount < bidBAmount) {
                          return 1;
                        }
                        if (bidAAmount > bidBAmount) {
                          return -1;
                        }
                        return 0;
                      })?.[0]?.Bid_Amount
                  }
                  refetchSelectedCar={refetchSelectedCar}
                  setRefetchSelectedCar={setRefetchSelectedCar}
                />
              ) : null}

              <CostBreakdown
                ownerRole={selectedCar?.owner_type?.toLowerCase?.()?.includes?.(USER_ROLES.seller) ? 
                  USER_ROLES.seller :
                  USER_ROLES.dealer
                }
              />

              <Box>
                <Typography
                  sx={{
                    color: theme.palette.primary.red,
                  }}
                >
                  {t('* Buyer fee will be $200 applicable after purchase')}
                </Typography>
              </Box>

              <SaleInformation
                data={{
                  ...(getAgentDetailsApiData?.data?.[0] || {}),
                  address: selectedCar?.Location,
                  date: selectedCar?.Live_Auction_DateTime,
                }}
                car={selectedCar}
              />
            </Box>
          </Grid>
          <Divider orientation={'vertical'} flexItem />
          {/* Main content area */}
          <Grid size={{ xl: 9.2 - 0.2, lg: 8 - 0.2, md: 12, sm: 12, xs: 12 }}>
            <CarDetails car={parsedVehicle(selectedCar)} />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default BuyNowCarDetail;
